/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import { embeddingModelApi, llmProviderApi } from '../../services/api';
import type { EmbeddingModel } from '../../types';
import { ConfirmDialog, ErrorAlert, LoadingSpinner, PageHeader } from '../common';
import { EmbeddingModelForm } from './EmbeddingModelForm';

const EmbeddingModelList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const [providerFilter, setProviderFilter] = useState<number | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<EmbeddingModel | undefined>();
  const [deletingModel, setDeletingModel] = useState<EmbeddingModel | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['embedding-models', providerFilter],
    queryFn: () => embeddingModelApi.list({ provider: providerFilter || undefined }),
  });

  const { data: providersData } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: () => llmProviderApi.list({}),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => embeddingModelApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embedding-models'] });
      setDeletingModel(null);
    },
  });

  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  return (
    <Box>
      <PageHeader
        title="Embedding Models"
        actions={
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              select
              size="small"
              value={providerFilter}
              onChange={(event) => setProviderFilter(event.target.value as number | '')}
              sx={{ minWidth: { xs: 120, sm: 220 } }}
              label="Provider"
            >
              <MenuItem value="">All Providers</MenuItem>
              {providersData?.results.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingModel(undefined);
                setFormOpen(true);
              }}
              size="small"
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Add Embedding Model
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                Add
              </Box>
            </Button>
          </Box>
        }
      />

      <EmbeddingModelForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingModel(undefined);
        }}
        model={editingModel}
      />

      <ConfirmDialog
        open={!!deletingModel}
        title="Delete Embedding Model"
        message={`Are you sure you want to delete "${deletingModel?.name}"? This action cannot be undone.`}
        onConfirm={async () => {
          if (deletingModel) {
            await deleteMutation.mutateAsync(deletingModel.id);
          }
        }}
        onCancel={() => setDeletingModel(null)}
      />

      {isLoading ? (
        <LoadingSpinner message="Loading embedding models..." />
      ) : isMobile ? (
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {data?.results.map((model) => (
            <Grid size={{ xs: 12, sm: 6 }} key={model.id}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pb: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' }, wordBreak: 'break-word', pr: 1 }}>
                      {model.name}
                    </Typography>
                    <Chip
                      label={model.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={model.is_active ? 'success' : 'default'}
                      sx={{ fontSize: '0.65rem', flexShrink: 0 }}
                    />
                  </Box>

                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {model.model}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    <Chip label={model.provider_name} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    <Chip label={`${model.dimensions} dims`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    ${(model.cost_per_million_tokens || 0).toFixed(2)}/1M tokens
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: 1, py: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingModel(model);
                      setFormOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeletingModel(model)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
          {data?.results.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography color="text.secondary" variant="body2">
                    No embedding models found.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model ID</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Dimensions</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.results.map((model) => (
                <TableRow key={model.id} hover>
                  <TableCell>{model.name}</TableCell>
                  <TableCell>{model.model}</TableCell>
                  <TableCell>
                    <Chip label={model.provider_name} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{model.dimensions}</TableCell>
                  <TableCell>
                    <Tooltip title="Cost per 1M tokens">
                      <span>${(model.cost_per_million_tokens || 0).toFixed(2)}/1M</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={model.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={model.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingModel(model);
                          setFormOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeletingModel(model)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {data?.results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No embedding models found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default EmbeddingModelList;